import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

// Default theme values
const defaultTheme = {
  colors: {
    primary: '#5D7061',
    secondary: '#D4AF37',
    accent: '#E8D5B7',
    background: '#FAF9F6',
    text: '#2D3436',
    muted: '#6B7280'
  },
  decorations: {
    pattern: 'floral',
    icons: ['flower', 'leaf'],
    border_style: 'elegant'
  },
  fonts: {
    heading: 'League Spartan',
    body: 'Manrope',
    accent: 'Great Vibes'
  },
  mood: 'elegant',
  coupleLogo: null,
  themeImage: null
};

const normalizeHex = (color, fallback = '#000000') => {
  const value = typeof color === 'string' ? color.trim() : '';
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toUpperCase();
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value.slice(1).split('').map((character) => character.repeat(2)).join('')}`.toUpperCase();
  }
  return fallback;
};

export const hexToRgb = (color) => {
  const hex = normalizeHex(color).slice(1);
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

const rgbChannels = (color) => {
  const { r, g, b } = hexToRgb(color);
  return `${r} ${g} ${b}`;
};

export const hexToHslChannels = (color) => {
  let { r, g, b } = hexToRgb(color);
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  let hue = 0;
  let saturation = 0;

  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5
      ? delta / (2 - max - min)
      : delta / (max + min);

    if (max === r) hue = (g - b) / delta + (g < b ? 6 : 0);
    if (max === g) hue = (b - r) / delta + 2;
    if (max === b) hue = (r - g) / delta + 4;
    hue /= 6;
  }

  return `${Math.round(hue * 360)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
};

const mixHex = (baseColor, mixedColor, mixedWeight) => {
  const base = hexToRgb(baseColor);
  const mixed = hexToRgb(mixedColor);
  const weight = Math.max(0, Math.min(1, mixedWeight));
  const channel = (name) => Math.round(base[name] * (1 - weight) + mixed[name] * weight)
    .toString(16)
    .padStart(2, '0');
  return `#${channel('r')}${channel('g')}${channel('b')}`.toUpperCase();
};

const readableForeground = (color) => {
  const { r, g, b } = hexToRgb(color);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.58 ? '#1F2933' : '#FFFFFF';
};

export const buildThemeCssVariables = (theme) => {
  const colors = { ...defaultTheme.colors, ...theme.colors };
  const fonts = { ...defaultTheme.fonts, ...theme.fonts };
  const softAccent = mixHex(colors.background, colors.accent, 0.28);
  const border = mixHex(colors.background, colors.primary, 0.16);
  const card = mixHex(colors.background, '#FFFFFF', 0.72);
  const sidebar = mixHex(colors.primary, '#111A17', 0.38);
  const sidebarRaised = mixHex(sidebar, '#FFFFFF', 0.07);
  const sidebarHover = mixHex(sidebar, '#FFFFFF', 0.12);
  const sidebarBorder = mixHex(sidebar, '#FFFFFF', 0.18);
  const sidebarActive = mixHex(colors.background, colors.primary, 0.16);
  const sidebarActiveText = mixHex(colors.primary, '#000000', 0.32);

  return {
    '--theme-primary': colors.primary,
    '--theme-secondary': colors.secondary,
    '--theme-accent': colors.accent,
    '--theme-background': colors.background,
    '--theme-text': colors.text,
    '--theme-muted': colors.muted,
    '--theme-font-heading': fonts.heading,
    '--theme-font-body': fonts.body,
    '--theme-font-accent': fonts.accent,
    '--theme-primary-rgb': rgbChannels(colors.primary),
    '--theme-secondary-rgb': rgbChannels(colors.secondary),
    '--theme-accent-rgb': rgbChannels(colors.accent),
    '--theme-background-rgb': rgbChannels(colors.background),
    '--theme-text-rgb': rgbChannels(colors.text),
    '--theme-muted-rgb': rgbChannels(colors.muted),
    '--theme-primary-light': `${colors.primary}20`,
    '--theme-secondary-light': `${colors.secondary}20`,
    '--theme-accent-light': `${colors.accent}40`,
    '--theme-sidebar': sidebar,
    '--theme-sidebar-raised': sidebarRaised,
    '--theme-sidebar-hover': sidebarHover,
    '--theme-sidebar-border': sidebarBorder,
    '--theme-sidebar-text': mixHex(sidebar, '#FFFFFF', 0.84),
    '--theme-sidebar-muted': mixHex(sidebar, '#FFFFFF', 0.62),
    '--theme-sidebar-active': sidebarActive,
    '--theme-sidebar-active-text': sidebarActiveText,
    '--background': hexToHslChannels(colors.background),
    '--foreground': hexToHslChannels(colors.text),
    '--card': hexToHslChannels(card),
    '--card-foreground': hexToHslChannels(colors.text),
    '--popover': hexToHslChannels(card),
    '--popover-foreground': hexToHslChannels(colors.text),
    '--primary': hexToHslChannels(colors.primary),
    '--primary-foreground': hexToHslChannels(readableForeground(colors.primary)),
    '--secondary': hexToHslChannels(colors.secondary),
    '--secondary-foreground': hexToHslChannels(readableForeground(colors.secondary)),
    '--muted': hexToHslChannels(softAccent),
    '--muted-foreground': hexToHslChannels(colors.muted),
    '--accent': hexToHslChannels(softAccent),
    '--accent-foreground': hexToHslChannels(colors.text),
    '--border': hexToHslChannels(border),
    '--input': hexToHslChannels(border),
    '--ring': hexToHslChannels(colors.primary),
    '--chart-1': hexToHslChannels(colors.primary),
    '--chart-2': hexToHslChannels(colors.secondary),
    '--chart-3': hexToHslChannels(colors.accent),
  };
};

const neutralWorkspaceVariables = {
  '--theme-font-heading': 'League Spartan',
  '--theme-font-body': 'Manrope',
  '--theme-font-accent': 'Great Vibes',
  '--theme-sidebar': '#1D2B27',
  '--theme-sidebar-raised': '#24352F',
  '--theme-sidebar-hover': '#293A34',
  '--theme-sidebar-border': '#30403A',
  '--theme-sidebar-text': '#DBE5E1',
  '--theme-sidebar-muted': '#9EAEA8',
  '--theme-sidebar-active': '#DCE8E3',
  '--theme-sidebar-active-text': '#17372C',
};

const ThemeContext = createContext({
  theme: defaultTheme,
  cssVariables: {},
  hasCustomTheme: false,
  isThemeEnabled: false,
});

export function ThemeProvider({ children }) {
  const { currentEvent, isEventManager } = useAuth();

  const themeData = useMemo(() => {
    const canApplyEventTheme = Boolean(currentEvent) && !isEventManager;
    const selected = canApplyEventTheme ? currentEvent?.theme?.selected_theme : null;
    const themeImage = canApplyEventTheme
      ? currentEvent?.theme?.selected_background_url
        || currentEvent?.theme?.custom_image_url
        || currentEvent?.theme?.generated_image_url
        || null
      : null;

    if (!selected) {
      return {
        theme: { ...defaultTheme, themeImage },
        hasCustomTheme: false,
        isThemeEnabled: Boolean(themeImage),
      };
    }

    return {
      theme: {
        colors: selected.colors || defaultTheme.colors,
        decorations: selected.decorations || defaultTheme.decorations,
        fonts: selected.fonts || defaultTheme.fonts,
        mood: selected.mood || 'elegant',
        name: selected.name,
        description: selected.description,
        previewGradient: selected.preview_gradient,
        coupleLogo: currentEvent.branding?.couple_logo_url,
        themeImage,
      },
      hasCustomTheme: true,
      isThemeEnabled: true,
    };
  }, [currentEvent, isEventManager]);

  // Generate CSS variables from theme
  const cssVariables = useMemo(() => {
    const variables = buildThemeCssVariables(themeData.theme);
    return isEventManager || !currentEvent
      ? { ...variables, ...neutralWorkspaceVariables }
      : variables;
  }, [themeData, isEventManager, currentEvent]);

  // Apply CSS variables to document root
  React.useEffect(() => {
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Load Google Fonts
    const { fonts } = themeData.theme;
    const fontFamilies = [fonts.heading, fonts.body, fonts.accent]
      .filter(Boolean)
      .map(f => f.replace(/ /g, '+'))
      .join('&family=');
    
    if (fontFamilies) {
      const linkId = 'theme-google-fonts';
      let link = document.getElementById(linkId);
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}:wght@400;500;600;700&display=swap`;
    }
  }, [cssVariables, themeData.theme]);

  return (
    <ThemeContext.Provider value={{ ...themeData, cssVariables }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useEventTheme() {
  return useContext(ThemeContext);
}

// Hook to get decorative icons based on theme
export function useThemeDecorations() {
  const { theme } = useEventTheme();
  
  const getIcon = (iconName) => {
    const iconMap = {
      flower: '🌸',
      rose: '🌹',
      leaf: '🍃',
      heart: '💕',
      peacock: '🦚',
      lotus: '🪷',
      crown: '👑',
      butterfly: '🦋',
      diamond: '💎',
      sun: '☀️',
      marigold: '🌼',
      diya: '🪔',
      mandala: '☸️',
      star: '⭐',
      feather: '🪶'
    };
    return iconMap[iconName] || '✨';
  };
  
  return {
    icons: theme.decorations?.icons?.map(getIcon) || ['🌸', '🍃'],
    pattern: theme.decorations?.pattern || 'floral',
    borderStyle: theme.decorations?.border_style || 'elegant'
  };
}
